import { FundingPolicyType } from '@shared';
import { getMaxMonthlyBenefitAmount } from '@shared/calculations/funding/projection/sharedPolicyConfig';

/**
 * OneAmerica Asset Care sizes its Acceleration (AOB) and Continuation (COB)
 * pools as `monthly benefit × benefit period`. The Face Amount IS the AOB pool
 * — the acceleration draws down the death benefit — and the COB dollar amount
 * is the COB pool, so both are derived values rather than independent inputs.
 *
 * When BOTH benefit-period durations and a monthly benefit are present, returns
 * the product for each pool so the form can show (and persist) the exact value
 * the projection uses. This mirrors the strategy's all-or-nothing duration gate
 * (`deriveAtIssuePools` / `usesDurationModel`): if either duration is missing (a
 * mixed record), returns null for BOTH pools — the strategy keeps such records
 * on the legacy dollar inputs (and raises the mixed-model warning), so the form
 * must not derive either pool, or it would silently overwrite the dollar value
 * the strategy actually uses.
 *
 * Scoped to single + joint Asset Care. Asset Flex is intentionally excluded:
 * its Face Amount is the death-benefit ceiling, independent of the Acceleration
 * pool (which the projection still derives from monthly × duration internally).
 */
export function getAssetCareDerivedPoolDollars(policy: PolicyFundingSource): {
  face: number | null;
  cob: number | null;
} {
  const isAssetCare =
    policy.policyType === FundingPolicyType.assetCare ||
    policy.policyType === FundingPolicyType.jointAssetCare;
  if (!isAssetCare) {
    return { face: null, cob: null };
  }

  const monthlyBenefit = getMaxMonthlyBenefitAmount(policy);
  if (!(monthlyBenefit > 0)) {
    return { face: null, cob: null };
  }

  const accelerationMonths = policy.policyAccelerationBenefitDurationMonths;
  const eobMonths = policy.policyEobBenefitDurationMonths;
  // All-or-nothing, matching the strategy's duration-model gate: a mixed record
  // (exactly one duration set) stays on the legacy dollar inputs + warns, so the
  // form must not derive either pool, or it silently overwrites the dollar value
  // the strategy actually uses.
  if (accelerationMonths == null || eobMonths == null) {
    return { face: null, cob: null };
  }
  return {
    face: Math.round(monthlyBenefit * accelerationMonths),
    cob: Math.round(monthlyBenefit * eobMonths),
  };
}
